// @ts-check
// Diálogo das telas dos fluxos (contrato 09 de fluxos, seção 17): cabeçalho com título e fechar, corpo com rolagem e
// rodapé com os botões. O foco entra no primeiro campo (ou no diálogo), Tab fica preso dentro, Esc fecha (um menu
// aberto dentro fecha primeiro) e o foco volta para quem abriu. No celular ocupa a tela inteira (flows.css).
import { html } from 'htm/preact';
import { createPortal } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { LightIconButton } from '../../ui/button.js';
import { t } from '../../i18n/index.js';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * @param {{ title: string, subtitle?: string | null, onClose: () => void, children?: any, footer?: any, size?: 'small' | 'medium' | 'large' | 'wide',
 *   testId?: string, className?: string, role?: 'dialog' | 'alertdialog', onSubmit?: (() => void) | null }} props
 *   onSubmit: Enter num campo de texto confirma (o corpo vira um formulário)
 */
export function FlowDialog({ title, subtitle = null, onClose, children, footer, size = 'small', testId, className = '', role = 'dialog', onSubmit = null }) {
  const dialogRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const titleId = useRef(`flow-dialog-title-${Math.random().toString(36).slice(2, 9)}`).current;

  useEffect(() => {
    const previousFocus = /** @type {HTMLElement | null} */ (document.activeElement);
    const frame = requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      const first = /** @type {HTMLElement | null} */ (dialog?.querySelector('[autofocus], [data-autofocus], input:not([type="radio"]):not([type="checkbox"]), textarea') ?? null);
      (first ?? dialog)?.focus({ preventScroll: true });
    });
    /** @param {KeyboardEvent} event */
    const handleKeyDown = (event) => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      // só o diálogo de cima responde (um diálogo aberto por outro fica por baixo)
      const dialogs = document.querySelectorAll('.flow-dialog');
      if (dialogs[dialogs.length - 1] !== dialog) return;
      if (event.key === 'Escape') {
        if (document.querySelector('[data-dropdown-surface]')) return;
        event.stopPropagation();
        closeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = /** @type {HTMLElement[]} */ ([...dialog.querySelectorAll(FOCUSABLE_SELECTOR)]).filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleKeyDown, true);
      if (previousFocus && document.contains(previousFocus)) previousFocus.focus({ preventScroll: true });
    };
  }, []);

  const body = html`<div class="flow-dialog-body">${children}</div>`;
  return createPortal(
    html`<div class="modal-backdrop flow-dialog-backdrop" onMouseDown=${(/** @type {MouseEvent} */ event) => event.target === event.currentTarget && onClose()}>
      <div ref=${dialogRef} class=${`flow-dialog ${className}`} data-size=${size} role=${role} aria-modal="true" aria-labelledby=${titleId} tabindex="-1"
        data-testid=${testId}>
        <header class="flow-dialog-header">
          <div class="flow-dialog-titles">
            <h2 class="flow-dialog-title" id=${titleId}>${title}</h2>
            ${subtitle ? html`<p class="flow-dialog-subtitle">${subtitle}</p>` : null}
          </div>
          <${LightIconButton} icon="IconX" size="medium" ariaLabel=${t('common.close')} onClick=${onClose} />
        </header>
        ${onSubmit
          ? html`<form class="flow-dialog-form" onSubmit=${(/** @type {Event} */ event) => {
              event.preventDefault();
              onSubmit();
            }}>${body}${footer ? html`<footer class="flow-dialog-footer">${footer}</footer>` : null}<button type="submit" hidden></button></form>`
          : html`${body}${footer ? html`<footer class="flow-dialog-footer">${footer}</footer>` : null}`}
      </div>
    </div>`,
    document.body,
  );
}
